import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCatalogItem } from '../api/catalog';
import { Header } from '../components/Header';
import { TagList } from '../components/TagList';
import type { CatalogItem } from '../types';

function isAbsoluteUrl(url: string) {
  return /^https?:\/\//i.test(url);
}

export function TestLandingPage() {
  const { slug = '' } = useParams();
  const [item, setItem] = useState<CatalogItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadItem() {
      try {
        setIsLoading(true);
        setNotFound(false);
        setError(null);
        const data = await getCatalogItem(slug);
        if (!cancelled) setItem(data);
      } catch (err) {
        console.error('Failed to load catalog item:', err);
        if (cancelled) return;
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError('לא הצלחנו לטעון את דף המבחן. נסו שוב.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadItem();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound && !isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main data-testid="test-not-found" className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:px-8">
          <div className="card p-10">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-600">לא נמצא</p>
            <h1 className="mt-3 text-3xl font-bold text-slate-950">המבחן הזה אינו זמין</h1>
            <p className="mt-4 text-slate-600">ייתכן שהוא הועבר, הוסר או אינו פעיל כרגע.</p>
            <Link to="/" className="btn-primary mt-8">חזרה לקטלוג</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="test-landing" className="min-h-screen bg-gradient-to-b from-white to-slate-100">
      <Header />
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <Link data-testid="test-back" to="/" className="text-sm font-semibold text-primary-700 hover:text-primary-800">
          → חזרה לקטלוג
        </Link>

        {isLoading && <div className="card mt-8 p-8 text-center text-slate-600">טוען פרטי מבחן…</div>}
        {error && !isLoading && <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>}

        {item && !isLoading && !error && (
          <section className="mt-8 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
            <div className="card p-8 sm:p-10">
              <div className="mb-8 flex h-20 w-20 items-center justify-center rounded-3xl bg-primary-50 text-5xl ring-1 ring-primary-100">
                {item.icon || '📚'}
              </div>
              <h1 data-testid="test-landing-title" className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl" dir="auto">
                {item.name}
              </h1>
              {item.subtitle && <p className="mt-4 text-xl font-semibold text-primary-700" dir="auto">{item.subtitle}</p>}
              {(item.long_description || item.description) && (
                <p className="mt-6 whitespace-pre-line text-lg leading-8 text-slate-700" dir="auto">
                  {item.long_description || item.description}
                </p>
              )}
              <TagList tags={item.tags} className="mt-8" />
              {item.cta_url && (
                <a
                  data-testid="test-cta"
                  href={item.cta_url}
                  target={item.external || isAbsoluteUrl(item.cta_url) ? '_blank' : undefined}
                  rel={item.external || isAbsoluteUrl(item.cta_url) ? 'noopener noreferrer' : undefined}
                  className="btn-primary mt-10"
                >
                  {item.cta_label || 'פתיחה'}
                </a>
              )}
            </div>

            <aside className="card p-8">
              <h2 className="text-xl font-bold text-slate-950">מה תקבלו</h2>
              {item.features.length > 0 ? (
                <ul className="mt-5 space-y-3">
                  {item.features.map((feature) => (
                    <li key={feature} className="flex gap-3 text-slate-700" dir="auto">
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-slate-600">פרטים נוספים בקרוב.</p>
              )}
            </aside>
          </section>
        )}
      </main>
    </div>
  );
}
